import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import {
  X,
  Save,
  Trash2,
  Pill,
  Check,
  ChevronsUpDown,
  Package,
  AlertCircle,
} from "lucide-react";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import { DoctorOrPracticeOwner } from "@/components/auth/RoleGuard";
import PatientSelector from "@/components/clinical-notes/PatientSelector";
import { useAuth } from "@/hooks/useAuth";
import { getUserDisplayName } from "@/utils/dataLookups";

// Helper function to strip HTML tags
const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

// Helper function to get a text summary from clinical note
const getNotePreview = (note) => {
  const consultation = stripHtml(note.consultation_notes || "");
  const diagnosis = stripHtml(note.diagnosis || "");
  const treatment = stripHtml(note.treatment_plan || "");

  // Prioritize showing content in this order
  if (diagnosis)
    return diagnosis.substring(0, 60) + (diagnosis.length > 60 ? "..." : "");
  if (consultation)
    return (
      consultation.substring(0, 60) + (consultation.length > 60 ? "..." : "")
    );
  if (treatment)
    return treatment.substring(0, 60) + (treatment.length > 60 ? "..." : "");
  return "Clinical Note";
};

export default function PrescriptionForm({ prescription, onSave, onClose }) {
  const { user, isDoctor } = useAuth();

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      patient_id: "",
      prescribing_doctor: "",
      prescription_date: new Date().toISOString().split("T")[0],
      visit_id: "",
      prescribed_items: [],
      notes: "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "prescribed_items",
  });

  // Watch patient_id for clinical notes query
  const patientId = watch("patient_id");

  // Fetch patients for the searchable dropdown
  const { data: patients = [], isLoading: patientsLoading } = useQuery({
    queryKey: ["patients"],
    queryFn: () => apiClient.get(ENDPOINTS.PATIENTS.LIST),
  });

  // Fetch clinical notes only when a patient is selected
  const { data: patientClinicalNotes = [], isLoading: clinicalNotesLoading } =
    useQuery({
      queryKey: ["clinical-notes-by-patient", patientId],
      queryFn: () =>
        apiClient.get(ENDPOINTS.CLINICAL_NOTES.BY_PATIENT(patientId)),
      enabled: !!patientId, // Only fetch when patient_id is provided
    });

  // Autopopulate prescribing doctor with signed-in doctor's name
  useEffect(() => {
    // Only autopopulate for new prescriptions when user is loaded
    if (user && isDoctor && !prescription) {
      const doctorName = getUserDisplayName(user);
      const currentPrescribingDoctor = watch("prescribing_doctor");
      if (doctorName && !currentPrescribingDoctor) {
        setValue("prescribing_doctor", doctorName);
      }
    }
  }, [user, isDoctor, prescription, setValue, watch]);

  useEffect(() => {
    if (prescription) {
      // Transform medications from backend format to form format
      const transformedMedications = (prescription.medications || []).map(
        (med) => ({
          id: med.id, // IMPORTANT: Include medication ID for updates
          medicineId: med.medicineId || "",
          medicine: null, // We'll need to fetch this if editing
          dosage: med.dosage || "",
          frequency: med.frequency || "",
          duration: med.duration || "",
          quantity: med.quantity || 1,
          instructions: med.instructions || "",
          dispenseLocation: med.dispenseLocation || "in_practice",
          refillsAllowed:
            med.refillsAllowed !== undefined ? med.refillsAllowed : 1,
        })
      );

      reset({
        patient_id: prescription.patientId || "",
        prescribing_doctor: prescription.doctorId || "",
        prescription_date: prescription.prescriptionDate
          ? new Date(prescription.prescriptionDate).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
        visit_id: prescription.visitId || "",
        prescribed_items: transformedMedications,
        notes: prescription.notes || "",
      });
    }
  }, [prescription, reset]);

  const addPrescribedItem = () => {
    append({
      medicineId: "",
      medicine: null, // Store full medicine object for display
      dosage: "",
      frequency: "",
      duration: "",
      quantity: 1, // Default to 1 pack
      instructions: "",
      dispenseLocation: "in_practice", // or "external"
      refillsAllowed: 1, // Default to 1 (minimum for first collection)
    });
  };

  // Fetch medicines for autocomplete
  const [medicineSearchTerm, setMedicineSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(medicineSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [medicineSearchTerm]);

  const { data: searchedMedicines = [], isLoading: isSearchingMedicines } =
    useQuery({
      queryKey: ["medicineSearch", debouncedSearchTerm],
      queryFn: () => {
        if (!debouncedSearchTerm || debouncedSearchTerm.length < 2) return [];
        return apiClient.get(ENDPOINTS.MEDICINES.SEARCH, {
          q: debouncedSearchTerm,
          limit: 20,
        });
      },
      enabled: debouncedSearchTerm.length >= 2,
    });
  // Fetch inventory to check stock availability
  const { data: inventoryItems = [] } = useQuery({
    queryKey: ["inventory"],
    queryFn: () => apiClient.get(ENDPOINTS.INVENTORY.LIST),
  });

  // Watch prescribed_items for medicine details query
  const prescribedItems = watch("prescribed_items");

  // Fetch medicine details for medications when editing
  const medicineIds = (prescribedItems || [])
    .map((item) => item?.medicineId)
    .filter(Boolean);

  const { data: medicineDetails = [] } = useQuery({
    queryKey: ["medicines", medicineIds],
    queryFn: async () => {
      if (medicineIds.length === 0) return [];
      const promises = medicineIds.map((id) =>
        apiClient.get(`${ENDPOINTS.MEDICINES.LIST}/${id}`)
      );
      return Promise.all(promises);
    },
    enabled: medicineIds.length > 0,
  });

  // Update prescribed items with medicine details when they're fetched
  useEffect(() => {
    if (medicineDetails.length > 0 && prescribedItems) {
      prescribedItems.forEach((item, index) => {
        if (item?.medicineId && !item?.medicine) {
          const medicine = medicineDetails.find(
            (m) => m.id === item.medicineId
          );
          if (medicine) {
            setValue(`prescribed_items.${index}.medicine`, medicine);
          }
        }
      });
    }
  }, [medicineDetails, prescribedItems, setValue]);

  const getStockInfo = (medicineId) => {
    if (!medicineId) return { available: 0, expired: 0, total: 0 };

    const items = inventoryItems.filter(
      (item) => item.medicineId === medicineId && item.currentStock > 0
    );

    const now = new Date();
    let available = 0;
    let expired = 0;

    items.forEach((item) => {
      const expiryDate = new Date(item.expiryDate);
      if (expiryDate < now) {
        expired += item.currentStock;
      } else {
        available += item.currentStock;
      }
    });

    return { available, expired, total: available + expired };
  };

  const onSubmit = (data) => {
    // Transform prescribed_items to match new API structure
    const medications = data.prescribed_items.map((item) => {
      const medication = {
        medicineId: item.medicineId,
        dosage: item.dosage,
        frequency: item.frequency,
        duration: item.duration || "",
        quantity: item.quantity || 1,
        instructions: item.instructions || "",
        dispenseLocation: item.dispenseLocation,
        refillsAllowed:
          item.refillsAllowed !== undefined ? item.refillsAllowed : 1,
      };

      // Include id if it exists (for updates)
      if (item.id) {
        medication.id = item.id;
      }

      return medication;
    });

    // Prepare data for new API
    const apiData = {
      patientId: data.patient_id,
      bookingId: data.visit_id || undefined,
      notes: data.notes || "",
      pharmacyNotes: data.notes || "",
      medications,
    };

    onSave(apiData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white/80 backdrop-blur-sm z-10 border-b">
          <CardTitle>{prescription ? "Edit" : "New"} Prescription</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Controller
                  name="patient_id"
                  control={control}
                  rules={{ required: "Patient is required" }}
                  render={({ field }) => (
                    <PatientSelector
                      patients={patients}
                      selectedPatient={field.value}
                      onPatientChange={field.onChange}
                      label="Patient *"
                      placeholder="Select patient..."
                      showAll={false}
                    />
                  )}
                />
                {errors.patient_id && (
                  <p className="text-sm text-red-600">
                    {errors.patient_id.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="prescribing_doctor">Prescribing Doctor *</Label>
                <Input
                  id="prescribing_doctor"
                  {...register("prescribing_doctor", {
                    required: "Prescribing doctor is required",
                  })}
                  placeholder="Dr. Smith"
                  className={errors.prescribing_doctor ? "border-red-500" : ""}
                />
                {errors.prescribing_doctor && (
                  <p className="text-sm text-red-600">
                    {errors.prescribing_doctor.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="prescription_date">Prescription Date *</Label>
                <Input
                  id="prescription_date"
                  type="date"
                  {...register("prescription_date", {
                    required: "Prescription date is required",
                  })}
                  className={errors.prescription_date ? "border-red-500" : ""}
                />
                {errors.prescription_date && (
                  <p className="text-sm text-red-600">
                    {errors.prescription_date.message}
                  </p>
                )}
              </div>
            </div>

            {/* Associated Visit - Only for doctors/practice owners */}
            <DoctorOrPracticeOwner>
              <div className="space-y-2">
                <Label htmlFor="visit_id">Associated Clinical Note</Label>
                <Controller
                  name="visit_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={!patientId || clinicalNotesLoading}
                    >
                      <SelectTrigger className="text-left">
                        <SelectValue
                          placeholder={
                            !patientId
                              ? "Select patient first"
                              : clinicalNotesLoading
                                ? "Loading clinical notes..."
                                : "Select clinical note (optional)"
                          }
                        >
                          {field.value &&
                            (() => {
                              const selectedNote = patientClinicalNotes.find(
                                (n) => n.id === field.value
                              );
                              if (selectedNote) {
                                return (
                                  <div className="flex flex-col items-start">
                                    <span className="font-medium">
                                      {new Date(
                                        selectedNote.date ||
                                          selectedNote.visit_date ||
                                          selectedNote.createdAt
                                      ).toLocaleDateString()}
                                    </span>
                                    <span className="text-sm text-slate-500">
                                      {getNotePreview(selectedNote)}
                                    </span>
                                  </div>
                                );
                              }
                              return field.value;
                            })()}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {patientClinicalNotes.map((note) => (
                          <SelectItem key={note.id} value={note.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {new Date(
                                  note.date || note.visit_date || note.createdAt
                                ).toLocaleDateString()}
                              </span>
                              <span className="text-sm text-slate-500">
                                {getNotePreview(note)}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                        {patientClinicalNotes.length === 0 &&
                          patientId &&
                          !clinicalNotesLoading && (
                            <div className="px-2 py-1.5 text-sm text-slate-500">
                              No clinical notes found for this patient
                            </div>
                          )}
                      </SelectContent>
                    </Select>
                  )}
                />
                {!patientId && (
                  <p className="text-sm text-slate-500">
                    Please select a patient first to see their clinical notes
                  </p>
                )}
              </div>
            </DoctorOrPracticeOwner>

            {/* Prescribed Items */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-slate-900">
                  Medications
                </h3>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={addPrescribedItem}
                    size="sm"
                    variant="outline"
                  >
                    <Pill className="w-4 h-4 mr-1" /> Add Medication
                  </Button>
                </div>
              </div>

              {fields.map((field, index) => {
                const item = watch(`prescribed_items.${index}`);
                return (
                  <div
                    key={field.id}
                    className="p-4 border border-slate-200 rounded-lg bg-slate-50/60"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-slate-800">
                        Medication #{index + 1}
                      </h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>

                    <div className="space-y-4">
                      {/* Medicine Selection */}
                      <div className="space-y-2">
                        <Label>Medicine *</Label>
                        <Controller
                          name={`prescribed_items.${index}.medicineId`}
                          control={control}
                          rules={{ required: "Medicine is required" }}
                          render={() => null}
                        />
                        {item?.medicine ? (
                          <div className="flex items-center gap-3 p-3 border border-green-200 bg-green-50 rounded-md">
                            <Package className="w-5 h-5 text-green-600 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">
                                {item.medicine.company} {item.medicine.product}
                              </p>
                              <p className="text-sm text-slate-600">
                                {item.medicine.strength}{" "}
                                {item.medicine.dosageForm} -{" "}
                                {item.medicine.packSize}
                                {item.medicine.schedule &&
                                  ` • Schedule ${item.medicine.schedule}`}
                              </p>
                            </div>
                            {item?.dispenseLocation === "in_practice" &&
                              (() => {
                                const stockInfo = getStockInfo(
                                  item?.medicineId
                                );
                                return (
                                  <div className="text-right flex flex-col gap-1">
                                    {stockInfo.available > 0 ? (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                                        <Check className="w-3 h-3" />
                                        {stockInfo.available} in stock
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                                        <AlertCircle className="w-3 h-3" />
                                        Out of stock
                                      </span>
                                    )}
                                    {stockInfo.expired > 0 && (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">
                                        <AlertCircle className="w-3 h-3" />
                                        {stockInfo.expired} expired
                                      </span>
                                    )}
                                  </div>
                                );
                              })()}
                            <button
                              type="button"
                              onClick={() => {
                                setValue(
                                  `prescribed_items.${index}.medicine`,
                                  null
                                );
                                setValue(
                                  `prescribed_items.${index}.medicineId`,
                                  ""
                                );
                              }}
                              className="text-slate-400 hover:text-slate-600"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                role="combobox"
                                className="w-full justify-between"
                              >
                                Search medicine catalog...
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-full p-0">
                              <Command shouldFilter={false}>
                                <CommandInput
                                  placeholder="Search medicines..."
                                  value={medicineSearchTerm}
                                  onValueChange={(value) => {
                                    setMedicineSearchTerm(value);
                                  }}
                                />
                                <CommandList>
                                  <CommandEmpty>
                                    {medicineSearchTerm.length < 2 ? (
                                      <span className="text-sm text-slate-500 p-2">
                                        Type at least 2 characters to search...
                                      </span>
                                    ) : isSearchingMedicines ? (
                                      "Searching..."
                                    ) : (
                                      `No medicines found`
                                    )}
                                  </CommandEmpty>
                                  <CommandGroup>
                                    {searchedMedicines.map((medicine) => (
                                      <CommandItem
                                        key={medicine.id}
                                        value={medicine.id}
                                        onSelect={() => {
                                          setValue(
                                            `prescribed_items.${index}.medicine`,
                                            medicine
                                          );
                                          setValue(
                                            `prescribed_items.${index}.medicineId`,
                                            medicine.id
                                          );
                                          setMedicineSearchTerm("");
                                        }}
                                      >
                                        <div className="flex flex-col">
                                          <span className="font-medium">
                                            {medicine.company}{" "}
                                            {medicine.product}
                                          </span>
                                          <span className="text-sm text-slate-500">
                                            {medicine.strength}{" "}
                                            {medicine.dosageForm} -{" "}
                                            {medicine.packSize}
                                            {medicine.genericName &&
                                              ` (${medicine.genericName})`}
                                          </span>
                                        </div>
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        )}
                        {errors.prescribed_items?.[index]?.medicineId && (
                          <p className="text-sm text-red-600">
                            {errors.prescribed_items[index].medicineId.message}
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Dosage (e.g., &quot;1 tablet&quot;) *</Label>
                          <Input
                            {...register(`prescribed_items.${index}.dosage`, {
                              required: "Dosage is required",
                            })}
                            placeholder="e.g., 1 tablet"
                            className={
                              errors.prescribed_items?.[index]?.dosage
                                ? "border-red-500"
                                : ""
                            }
                          />
                          {errors.prescribed_items?.[index]?.dosage && (
                            <p className="text-sm text-red-600">
                              {errors.prescribed_items[index].dosage.message}
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>Frequency *</Label>
                          <Input
                            {...register(
                              `prescribed_items.${index}.frequency`,
                              {
                                required: "Frequency is required",
                              }
                            )}
                            placeholder="e.g., twice daily"
                            className={
                              errors.prescribed_items?.[index]?.frequency
                                ? "border-red-500"
                                : ""
                            }
                          />
                          {errors.prescribed_items?.[index]?.frequency && (
                            <p className="text-sm text-red-600">
                              {errors.prescribed_items[index].frequency.message}
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>Duration</Label>
                          <Input
                            {...register(`prescribed_items.${index}.duration`)}
                            placeholder="e.g., 5 days"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Packs per Fill</Label>
                          <Controller
                            name={`prescribed_items.${index}.quantity`}
                            control={control}
                            render={({ field }) => (
                              <Input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={field.value || ""}
                                onChange={(e) => {
                                  const value = e.target.value.replace(
                                    /[^0-9]/g,
                                    ""
                                  );
                                  // Allow empty string during editing
                                  field.onChange(
                                    value === "" ? "" : parseInt(value)
                                  );
                                }}
                                onBlur={(e) => {
                                  // Default to 1 if empty when field loses focus
                                  const value = e.target.value.replace(
                                    /[^0-9]/g,
                                    ""
                                  );
                                  if (value === "") {
                                    field.onChange(1);
                                  }
                                  field.onBlur();
                                }}
                              />
                            )}
                          />
                          <p className="text-xs text-slate-500">
                            Number of packs dispensed per fill
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label>Total Fills</Label>
                          <Controller
                            name={`prescribed_items.${index}.refillsAllowed`}
                            control={control}
                            render={({ field }) => (
                              <Input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={field.value || ""}
                                onChange={(e) => {
                                  const value = e.target.value.replace(
                                    /[^0-9]/g,
                                    ""
                                  );
                                  // Allow empty string during editing
                                  field.onChange(
                                    value === "" ? "" : parseInt(value)
                                  );
                                }}
                                onBlur={(e) => {
                                  // Default to 1 if empty when field loses focus
                                  const value = e.target.value.replace(
                                    /[^0-9]/g,
                                    ""
                                  );
                                  if (value === "") {
                                    field.onChange(1);
                                  }
                                  field.onBlur();
                                }}
                              />
                            )}
                          />
                          <p className="text-xs text-slate-500">
                            How many times patient can collect (default: 1)
                          </p>
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label>Dispense Location *</Label>
                          <Controller
                            name={`prescribed_items.${index}.dispenseLocation`}
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                              <Select
                                value={field.value}
                                onValueChange={field.onChange}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="in_practice">
                                    Dispense at Practice
                                  </SelectItem>
                                  <SelectItem value="external">
                                    External Pharmacy
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 space-y-2">
                      <Label>Special Instructions (SIG)</Label>
                      <Textarea
                        {...register(`prescribed_items.${index}.instructions`)}
                        placeholder="e.g., Take with a full glass of water. Avoid direct sunlight."
                        rows={2}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes for Pharmacy</Label>
              <Textarea
                id="notes"
                {...register("notes")}
                placeholder="e.g., Please use child-proof caps."
                rows={3}
              />
            </div>

            {/* Actions */}
            <div className="flex flex-col items-end gap-3 pt-6">
              {fields.length === 0 && (
                <p className="text-sm text-amber-600">
                  Please add at least one medication to save the prescription
                </p>
              )}
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={fields.length === 0}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Prescription
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
